// Renderer-side access to the Electron `window.desktop` bridge (see
// electron/preload.js). Everything here is a no-op in a normal browser, so
// importing it from shared code is safe for the web/wwwroot build.

export const isElectron =
  typeof navigator !== "undefined" && /electron/i.test(navigator.userAgent);

/**
 * The origin to use for OAuth redirect URIs. In Electron the renderer origin is
 * app:// (or the dev server), which isn't a valid redirect target and isn't
 * registered on the backend's Google client — so pin it to the dev web origin.
 * In a browser, use the real page origin. Pure (exported for tests).
 */
export const resolveOAuthOrigin = (
  userAgent: string,
  origin: string
): string =>
  /electron/i.test(userAgent || "") ? "https://dev.propertelligent.com" : origin;

interface DesktopBridge {
  isElectron: boolean;
  platform: string;
  authStore: {
    get: () => Promise<Record<string, string>>;
    set: (snapshot: Record<string, string>) => Promise<void>;
    clear: () => Promise<void>;
  };
  gmailOAuth: (
    authUrl: string
  ) => Promise<{ code?: string; error?: string }>;
}

export const desktop: DesktopBridge | undefined =
  typeof window !== "undefined" ? (window as any).desktop : undefined;

// Session keys written at login (see components/user/Login.tsx and
// services/login.service.ts). Persisting these keeps a desktop user signed in
// across app restarts, since sessionStorage otherwise clears on window close.
const AUTH_KEYS = [
  "1", // UserLoginData.token
  "2", // user_name
  "3", // user_id
  "4", // organization_id
  "5", // currency_symbol
  "6", // tenant_id
  "7", // permissions
  "userRoleId",
  "userOnboarded",
  "tenantInfo",
  "actualUserId",
  "organizationUserId",
  "contractorInfo",
];

function snapshot(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of AUTH_KEYS) {
    const value = sessionStorage.getItem(key);
    if (value !== null) out[key] = value;
  }
  return out;
}

/**
 * Hydrate sessionStorage from the persistent store, then install write-through
 * so later auth writes (login) and sessionStorage.clear() (logout) stay synced.
 * Call once, before rendering, so App.tsx reads a warm session on cold start.
 */
export async function hydrateDesktopAuth(): Promise<void> {
  if (!desktop) return;

  try {
    const saved = await desktop.authStore.get();
    for (const [key, value] of Object.entries(saved || {})) {
      // Don't clobber anything a same-run write already set.
      if (sessionStorage.getItem(key) === null) {
        sessionStorage.setItem(key, value as string);
      }
    }
  } catch (err) {
    console.error("[desktop] auth hydrate failed", err);
  }

  const origSet = sessionStorage.setItem.bind(sessionStorage);
  const origRemove = sessionStorage.removeItem.bind(sessionStorage);
  const origClear = sessionStorage.clear.bind(sessionStorage);

  sessionStorage.setItem = (key: string, value: string) => {
    origSet(key, value);
    if (AUTH_KEYS.includes(key)) void desktop.authStore.set(snapshot());
  };
  sessionStorage.removeItem = (key: string) => {
    origRemove(key);
    if (AUTH_KEYS.includes(key)) void desktop.authStore.set(snapshot());
  };
  sessionStorage.clear = () => {
    origClear();
    void desktop.authStore.clear();
  };
}
